import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useGetMeetingGuidanceQuery } from "../api/meetingGuidanceApi";
import type {
  MeetingContinuitySignal,
  MeetingGuidanceWidgetProps,
  MeetingIntakeEntry,
  MeetingPersonalContextSignal,
  MeetingRelationshipSignal,
} from "../types";

type RelationshipDisplaySignal =
  | MeetingRelationshipSignal
  | MeetingContinuitySignal
  | MeetingPersonalContextSignal;

const formatLabel = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatEntry = (entry: MeetingIntakeEntry) =>
  `${entry.text} (${formatLabel(entry.priority)})`;

const formatQuestionSource = (source: string) =>
  source === "ai_assisted" ? "AI-assisted" : "Rules fallback";

const formatSignalSource = (source: string) => {
  const labels: Record<string, string> = {
    questionnaire: "Questionnaire",
    client_note: "Client note",
    meeting_intake: "Meeting intake",
    plan_context: "Plan context",
  };
  return labels[source] ?? formatLabel(source);
};

export const MeetingGuidanceWidget = ({
  planResultRootId,
  planResultTypeId,
  onClose,
}: MeetingGuidanceWidgetProps) => {
  const { data, isLoading, isError, error, refetch } =
    useGetMeetingGuidanceQuery({
      planResultRootId,
      planResultTypeId,
    });

  if (isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4SBold">Loading meeting guidance...</Typography>
      </Box>
    );
  }

  if (isError || !data) {
    const errorMessage = String(
      (error as { data?: { error?: string } } | undefined)?.data?.error ??
        "Failed to load meeting guidance.",
    );

    return (
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2 }}>
        <Alert severity="error">{errorMessage}</Alert>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" onClick={() => refetch()}>
            Retry
          </Button>
          {onClose && (
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  const {
    prep_brief: prepBrief,
    meeting_context: meetingContext,
    meeting_intake: meetingIntake,
    relationship_signal_guidance: relationshipSignalGuidance,
    ai_question_guidance: aiQuestionGuidance,
    live_companion: liveCompanion,
  } = data;
  const hasExplicitIntake =
    meetingIntake.client_topics.length > 0 ||
    meetingIntake.advisor_priorities.length > 0;
  const totalRelationshipSignals =
    relationshipSignalGuidance.relationship_signals.length +
    relationshipSignalGuidance.continuity_signals.length +
    relationshipSignalGuidance.personal_context_signals.length;

  const renderSignalSection = (
    title: string,
    signals: RelationshipDisplaySignal[],
  ) => (
    <Box>
      <Typography variant="par01SemiBold" component="div" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {signals.length > 0 ? (
        <Stack spacing={1.5}>
          {signals.map((signal, index) => (
            <Box key={`${title}-${signal.signal_id}-${index}`}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={formatSignalSource(signal.source)} size="small" />
                <Chip
                  label={`Priority: ${formatLabel(signal.priority)}`}
                  size="small"
                />
              </Stack>
              <Typography variant="par02Regular" component="div" sx={{ mt: 0.5 }}>
                {signal.label}: {signal.detail}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="par02Regular" color="text.secondary">
          No signals available.
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Stack spacing={1} sx={{ maxWidth: 840, minWidth: 0 }}>
          <Typography variant="h3SMedium" component="div">
            Meeting Guidance
          </Typography>
          <Typography
            variant="par02Regular"
            color="text.secondary"
            component="div"
          >
            {meetingContext.plan.plan_name}
          </Typography>
          <Typography variant="par02Regular" component="div">
            {prepBrief.overview}
          </Typography>
        </Stack>
        {onClose && (
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" component="div" sx={{ mb: 1 }}>
            Relationship Signals
          </Typography>
          <Typography
            variant="par02Regular"
            color="text.secondary"
            component="div"
            sx={{ mb: 2 }}
          >
            Advisor-facing relationship, continuity, and personal context from
            Caravel planning data.
          </Typography>
          {relationshipSignalGuidance.warnings.map((warning) => (
            <Alert key={warning} severity="info" sx={{ mb: 2 }}>
              {warning}
            </Alert>
          ))}
          {totalRelationshipSignals > 0 ? (
            <Stack spacing={2.5}>
              {renderSignalSection(
                "Relationship context",
                relationshipSignalGuidance.relationship_signals,
              )}
              {renderSignalSection(
                "Prior meeting continuity",
                relationshipSignalGuidance.continuity_signals,
              )}
              {renderSignalSection(
                "Personal context",
                relationshipSignalGuidance.personal_context_signals,
              )}
            </Stack>
          ) : (
            <Typography variant="par02Regular">
              No additional relationship signals were available for this
              meeting.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Classification
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label={`Level: ${formatLabel(prepBrief.classification.client_level)}`}
            />
            <Chip
              label={`Life Stage: ${formatLabel(prepBrief.classification.life_stage)}`}
            />
            <Chip
              label={`Household: ${formatLabel(prepBrief.classification.household_type)}`}
            />
            <Chip
              label={`Review: ${formatLabel(prepBrief.classification.review_recency)}`}
            />
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h5SMedium" sx={{ mb: 2 }}>
              Agenda
            </Typography>
            <List disablePadding>
              {prepBrief.agenda.map((item, index) => (
                <Box key={item.item_id}>
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`${index + 1}. ${item.title}`}
                      secondary={
                        item.is_required
                          ? "Core agenda item"
                          : "Conditional agenda item"
                      }
                      primaryTypographyProps={{
                        sx: { color: "caravelOrangePrimary" },
                      }}
                    />
                  </ListItem>
                  {item.item_id === "priority_topics" && hasExplicitIntake && (
                    <Box sx={{ pl: 4, pb: 2 }}>
                      <Typography
                        variant="par02Regular"
                        color="text.secondary"
                        component="div"
                        sx={{ mb: 1 }}
                      >
                        Explicit meeting intent to cover in this section:
                      </Typography>
                      <List
                        disablePadding
                        sx={{ listStyleType: "disc", pl: 3 }}
                      >
                        {meetingIntake.client_topics.map(
                          (topic, topicIndex) => (
                            <ListItem
                              key={`agenda-client-topic-${topicIndex}-${topic.text}`}
                              disableGutters
                              sx={{ display: "list-item", py: 0.25 }}
                            >
                              <ListItemText
                                primary={`Client topic: ${formatEntry(topic)}`}
                                primaryTypographyProps={{
                                  variant: "par02Regular",
                                }}
                              />
                            </ListItem>
                          ),
                        )}
                        {meetingIntake.advisor_priorities.map(
                          (priority, priorityIndex) => (
                            <ListItem
                              key={`agenda-advisor-priority-${priorityIndex}-${priority.text}`}
                              disableGutters
                              sx={{ display: "list-item", py: 0.25 }}
                            >
                              <ListItemText
                                primary={`Advisor priority: ${formatEntry(priority)}`}
                                primaryTypographyProps={{
                                  variant: "par02Regular",
                                }}
                              />
                            </ListItem>
                          ),
                        )}
                      </List>
                    </Box>
                  )}
                  {index < prepBrief.agenda.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h5SMedium" sx={{ mb: 2 }}>
              Plan Snapshot
            </Typography>
            <Stack spacing={1}>
              <Typography variant="par02Regular">
                Goals: {meetingContext.plan.goal_count}
              </Typography>
              <Typography variant="par02Regular">
                Accounts: {meetingContext.plan.account_count}
              </Typography>
              <Typography variant="par02Regular">
                Household:{" "}
                {meetingContext.household.is_family_plan
                  ? "Family"
                  : "Individual"}
              </Typography>
              <Typography variant="par02Regular">
                Tier: {meetingContext.household.client_tier ?? "Unknown"}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Activated Topics
          </Typography>
          <Stack spacing={2}>
            {prepBrief.activated_topics.map((topic) => (
              <Box
                key={topic.topic_id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography variant="par01SemiBold" component="div">
                  {topic.title}
                </Typography>
                <Typography
                  variant="par02Regular"
                  color="text.secondary"
                  component="div"
                >
                  Agenda Section: {formatLabel(topic.agenda_item_id)}
                </Typography>
                <Typography variant="par02Regular" component="div">
                  {topic.reason}
                </Typography>
              </Box>
            ))}
            {prepBrief.activated_topics.length === 0 && (
              <Typography variant="par02Regular">
                No dynamic topics were activated for this meeting.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" component="div" sx={{ mb: 1 }}>
            Question Recommendations
          </Typography>
          <Typography
            variant="par02Regular"
            color="text.secondary"
            component="div"
            sx={{ mb: 2 }}
          >
            Advisor-facing suggested questions for eligible planning domains.
          </Typography>
          {aiQuestionGuidance.warnings.map((warning) => (
            <Alert key={warning} severity="info" sx={{ mb: 2 }}>
              {warning}
            </Alert>
          ))}
          {aiQuestionGuidance.questions.length > 0 ? (
            <Stack spacing={2}>
              {aiQuestionGuidance.questions.map((question, index) => (
                <Box
                  key={`${question.domain}-${index}-${question.question_text}`}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mb: 1 }}
                  >
                    <Chip label={formatLabel(question.domain)} />
                    <Chip label={formatQuestionSource(question.source)} />
                    <Chip
                      label={`Priority: ${formatLabel(question.priority)}`}
                    />
                    {question.agenda_item_id && (
                      <Chip
                        label={`Agenda: ${formatLabel(question.agenda_item_id)}`}
                      />
                    )}
                  </Stack>
                  <Typography variant="par01SemiBold" component="div">
                    {question.question_text}
                  </Typography>
                  <Typography
                    variant="par02Regular"
                    color="text.secondary"
                    component="div"
                  >
                    {question.reason}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="par02Regular">
              No advisor question recommendations were generated for this
              meeting.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" component="div" sx={{ mb: 1 }}>
            Live Meeting Companion
          </Typography>
          <Typography
            variant="par02Regular"
            color="text.secondary"
            component="div"
            sx={{ mb: 3 }}
          >
            {liveCompanion.overview}
          </Typography>
          <Stack spacing={3}>
            {liveCompanion.sections.map((section) => (
              <Box key={section.agenda_item_id}>
                <Typography
                  variant="par01SemiBold"
                  component="div"
                  sx={{ mb: 1 }}
                >
                  {section.title}
                </Typography>
                <List disablePadding sx={{ listStyleType: "disc", pl: 3 }}>
                  {section.prompts.map((prompt, index) => (
                    <ListItem
                      key={`${section.agenda_item_id}-prompt-${index}`}
                      disableGutters
                      sx={{ display: "list-item", py: 0.25 }}
                    >
                      <ListItemText
                        primary={prompt}
                        primaryTypographyProps={{ variant: "par02Regular" }}
                      />
                    </ListItem>
                  ))}
                </List>
                {section.watchouts.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <Typography variant="par02Regular" color="text.secondary">
                      Watch-outs
                    </Typography>
                    <List disablePadding sx={{ listStyleType: "disc", pl: 3 }}>
                      {section.watchouts.map((watchout, index) => (
                        <ListItem
                          key={`${section.agenda_item_id}-watchout-${index}`}
                          disableGutters
                          sx={{ display: "list-item", py: 0.25 }}
                        >
                          <ListItemText
                            primary={watchout}
                            primaryTypographyProps={{ variant: "par02Regular" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Recommendation Reminders
          </Typography>
          {liveCompanion.recommendation_reminders.length > 0 ? (
            <List disablePadding sx={{ listStyleType: "disc", pl: 3 }}>
              {liveCompanion.recommendation_reminders.map((reminder, index) => (
                <ListItem
                  key={`${index}-${reminder}`}
                  disableGutters
                  sx={{ display: "list-item", py: 0.5 }}
                >
                  <ListItemText
                    primary={reminder}
                    primaryTypographyProps={{ variant: "par02Regular" }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="par02Regular">
              No recommendation reminders were generated for this meeting.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Advisor Notes
          </Typography>
          <List disablePadding sx={{ listStyleType: "disc", pl: 3 }}>
            {prepBrief.advisor_notes.map((note, index) => (
              <ListItem
                key={`${index}-${note}`}
                disableGutters
                sx={{ display: "list-item", py: 0.5 }}
              >
                <ListItemText
                  primary={note}
                  primaryTypographyProps={{ variant: "par02Regular" }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};
